import { Api } from '../Api';
import { DataFrame, Method, Status } from '../DataFrame';
import { ExalusConnectionService } from './ExalusConnectionService';
import { IExalusConnectionService } from './IExalusConnectionService';
import { ILocalStorageService } from './ILocalStorageService';
import { IRemoteStorageService, RemoteStorageDataEntry } from './IRemoteStorageService';
import { LocalStorageService } from './LocalStorageService';
export class RemoteStorageService implements IRemoteStorageService {
	public static readonly ServiceName: string = "RemoteStorageService";
	private _connection: IExalusConnectionService = null as any;
	private _localDb: ILocalStorageService = null as any;

	constructor() {
		this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
		this._localDb = Api.Get<ILocalStorageService>(LocalStorageService.ServiceName);
	}

	private GetLocalDbName(resourceGuid: string, isGlobalForAllUsers: boolean): string {
		return `${resourceGuid}_${isGlobalForAllUsers}`;
	}

	async SaveAsync(resourceGuid: string, isGlobalForAllUsers: boolean, data: any): Promise<Status> {
		try {
			let req = new SaveConfigRequestFrame(resourceGuid, isGlobalForAllUsers, data);
			let result = await this._connection.SendAndWaitForResponseAsync<any>(req, 35000, false);

			switch (result.Status) {
				case Status.OK:
					const dat = new RemoteStorageDataEntryResponse();
					dat.ResourceGuid = resourceGuid;
					dat.Data = JSON.stringify(data);
					dat.IsGlobal = isGlobalForAllUsers;
					this._localDb.Save(RemoteStorageService.ServiceName, this.GetLocalDbName(resourceGuid, isGlobalForAllUsers), dat);
					break;
			}

			return result.Status as Status;
		} catch {
			return Status.FatalError;
		}
	}

	async ReadAsync<T>(resourceGuid: string, isGlobalForAllUsers: boolean, skipLocalChache: boolean): Promise<RemoteStorageDataEntry<T> | Status> {
		try {
			let req = new ConfigRequestFrame(resourceGuid, isGlobalForAllUsers);

			if (!skipLocalChache) {
				const res = this._localDb.Read(RemoteStorageService.ServiceName, this.GetLocalDbName(resourceGuid, isGlobalForAllUsers));

				if (res != null) {
					const d = res as RemoteStorageDataEntryResponse;
					const r = new RemoteStorageDataEntry<T>();
					r.Data = JSON.parse(d.Data);
					r.IsGlobal = d.IsGlobal;
					r.ResourceGuid = d.ResourceGuid;
					return r;
				}
			}

			let result = (await this._connection.SendAndWaitForResponseAsync<any>(req, 35000, false)) as RemoteStorageDataEntryResponse;

			switch (result.Status) {
				case Status.OK:
					const ret = new RemoteStorageDataEntry<T>();
					ret.ResourceGuid = result.ResourceGuid;
					ret.Data = JSON.parse(result.Data);
					ret.IsGlobal = result.IsGlobal;
					return ret;

				default:
					return result.Status as Status;
			}
		} catch {
			return Status.FatalError;
		}
	}

	async RemoveAsync(resourceGuid: string, isGlobalForAllUsers: boolean): Promise<Status> {
		try {
			let req = new RemoveConfigRequestFrame(resourceGuid, isGlobalForAllUsers);
			let result = await this._connection.SendAndWaitForResponseAsync<any>(req, 35000, false);
			return result.Status as Status;
		} catch {
			return Status.FatalError;
		}
	}

	GetServiceName(): string {
		return RemoteStorageService.ServiceName;
	}
}

class SaveConfigRequestFrame extends DataFrame<string>
{
	constructor(resourceGuid: string, isGlobalForAllUsers: boolean, data: any) {
		super();
		this.Resource = "/sync/config/";
		this.Method = Method.Put;
		this.ResourceGuid = resourceGuid;
		this.Global = isGlobalForAllUsers;
		this.Data = JSON.stringify(data);
	}

	ResourceGuid: string = "";
	Global: boolean = false;
}

class ConfigRequestFrame extends DataFrame<string>
{
	constructor(resourceGuid: string, isGlobalForAllUsers: boolean) {
		super();
		this.Resource = "/sync/config/";
		this.Method = Method.Get;
		this.ResourceGuid = resourceGuid;
		this.Global = isGlobalForAllUsers;
	}

	ResourceGuid: string = "";
	Global: boolean = false;
}

class RemoveConfigRequestFrame extends DataFrame<string>
{
	constructor(resourceGuid: string, isGlobalForAllUsers: boolean) {
		super();
		this.Resource = "/sync/config/";
		this.Method = Method.Delete;
		this.ResourceGuid = resourceGuid;
		this.Global = isGlobalForAllUsers;
	}

	ResourceGuid: string = "";
	Global: boolean = false;
}

export class RemoteStorageDataEntryResponse extends DataFrame<string> {
	ResourceGuid: string = "";
	Data: string = "";
	IsGlobal: boolean = false;
	InsertTime: Date = new Date();
}
